import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { canModifyDeal } from '../../utils/permissions';
import { SharedTable } from '../common';

const DealsTableWithShared = ({
  deals,
  selectedDeals,
  setSelectedDeals,
  selectAll,
  setSelectAll,
  handleSelectAll,
  setSelectedDealId,
  setIsViewEditModalOpen,
  formatCurrency,
  updateDealStage,
  users,
  setDeals,
  sortBy,
  sortOrder,
  handleSort,
  showColumnModal,
  setShowColumnModal,
  pagination,
  onPageChange,
  onLimitChange, // Add onLimitChange prop here
  loading = false
}) => {
  // Fetch pipeline stages for dropdowns
  const [pipelineStages, setPipelineStages] = useState([]);
  
  useEffect(() => {
    const fetchPipelineStages = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get('/api/deals/stages/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        setPipelineStages(response.data);
      } catch (error) {
        console.error('Error fetching pipeline stages:', error);
      }
    };
    
    fetchPipelineStages();
  }, []);
  // Column configuration with default order and width
  const defaultColumns = [
    { id: 'title', label: 'Title', visible: true, width: 160 },
    { id: 'lead_name', label: 'Customer', visible: true, width: 160 },
    { id: 'course', label: 'Course', visible: true, width: 150 },
    { id: 'country', label: 'Country', visible: true, width: 100 },
    { id: 'value', label: 'Value', visible: true, width: 120, sortable: true },
    { id: 'value_companycurrency', label: 'Value in Company Currency', visible: true, width: 160, sortable: true, sortField: 'value_companycurrency' },
    { id: 'payment_method', label: 'Payment', visible: true, width: 100 },
    { id: 'stage_name', label: 'Stage', visible: true, width: 140 },
    { id: 'original_source', label: 'Original Source', visible: true, width: 130 },
    { id: 'latest_source', label: 'Latest Source', visible: true, width: 130 },
    { id: 'lesson_progress', label: 'Lessons', visible: true, width: 120 },
    { id: 'owner_name', label: 'Deal Owner', visible: true, width: 130 },
    { id: 'created_at', label: 'Created', visible: true, sortable: true, width: 100 },
    { id: 'paid_date', label: 'Paid Date', visible: true, sortable: true, width: 100 }
  ];

  // Load column order from localStorage or use default
  const [columns, setColumns] = useState(() => {
    const savedColumns = localStorage.getItem('dealsTableColumns');
    return savedColumns ? JSON.parse(savedColumns) : defaultColumns;
  });

  // Keep a reference to default columns for reset functionality
  const defaultColumnsRef = useRef(defaultColumns);

  // Save column configuration to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dealsTableColumns', JSON.stringify(columns));
  }, [columns]);
  
  // Handle reset to default
  const handleResetColumns = () => {
    setColumns([...defaultColumnsRef.current]);
  };

  // Handle individual deal selection
  const handleDealSelection = (dealId) => {
    if (selectedDeals.includes(dealId)) {
      setSelectedDeals(selectedDeals.filter(id => id !== dealId));
      setSelectAll(false);
    } else {
      setSelectedDeals([...selectedDeals, dealId]);
      if (selectedDeals.length + 1 === deals.length) {
        setSelectAll(true);
      }
    }
  };

  // Render cell content based on column ID
  const renderCell = (deal, columnId) => {
    switch (columnId) {
      case 'title':
        return (
          <div 
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer truncate max-w-xs"
            onClick={() => {
              setSelectedDealId(deal.id);
              setIsViewEditModalOpen(true);
            }}
            title="Click to view or edit deal details"
          >
            {deal.title}
          </div>
        );
      case 'lead_name':
        return (
          <div className="text-sm text-gray-900">
            {deal.lead_id ? (
              <div className="flex items-center">
                <a 
                  href={`/leads/${deal.lead_id}`} 
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={(e) => {
                    // If not using the right mouse button or command/ctrl key
                    if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                      e.preventDefault();
                      window.open(`/leads/${deal.lead_id}`, '_blank');
                    }
                  }}
                  title="Open lead in new tab"
                >
                  {deal.lead_name || 'View Lead'}
                </a>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-3 w-3 ml-1 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                  />
                </svg>
              </div>
            ) : (
              deal.lead_name || 'N/A'
            )}
          </div>
        );
      case 'course':
        return (
          <div className="text-sm text-gray-900">{deal.course || '-'}</div>
        );
      case 'country':
        return (
          <div className="text-sm text-gray-900">{deal.country || '-'}</div>
        );
      case 'value':
        return (
          <div className="text-sm">
            <div className="text-gray-900">{formatCurrency(deal.value, deal.currency)}</div>
            {/* Show refunded amount if in Refunded stage */}
            {deal.stage_name === 'Refunded' && deal.refunded_amount && (
              <div className="text-xs text-red-600 mt-1">
                Refunded: {formatCurrency(deal.refunded_amount, deal.currency)}
              </div>
            )}
          </div>
        );
      case 'value_companycurrency':
        return (
          <div className="text-sm text-gray-900">
            {formatCurrency(deal.value_companycurrency, deal.company_currency || 'NOK')}
          </div>
        );
      case 'payment_method':
        return (
          <div className="text-sm text-gray-900">{deal.payment_method || '-'}</div>
        );
      case 'original_source':
        return (
          <div className="text-sm text-gray-900">{deal.original_source || '-'}</div>
        );
      case 'latest_source':
        return (
          <div className="text-sm text-gray-900">{deal.latest_source || '-'}</div>
        );
      case 'stage_name':
        return (
          <div className="text-sm cursor-pointer inline-flex items-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <select
              value={deal.stage_name}
              onChange={(e) => updateDealStage(deal.id, e.target.value)}
              className={`appearance-none w-32 py-1 px-2 text-xs border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full ${
                // Find the matching stage to get its color, or default to blue
                pipelineStages.find(stage => stage.name === deal.stage_name)?.color
                  ? `bg-opacity-20 text-black`
                  : 'bg-blue-100 text-blue-800'
              }`}
              style={{
                backgroundColor: pipelineStages.find(stage => stage.name === deal.stage_name)?.color 
                  ? `${pipelineStages.find(stage => stage.name === deal.stage_name)?.color}30` // Add 30 for 20% opacity
                  : '',
                color: pipelineStages.find(stage => stage.name === deal.stage_name)?.color || '#1e40af'
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={!canModifyDeal('update', deal)}
            >
              {pipelineStages.length > 0 
                ? pipelineStages.map(stage => (
                    <option key={stage.id} value={stage.name}>{stage.name}</option>
                  ))
                : [
                    <option key="loading" value={deal.stage_name}>{deal.stage_name}</option>
                  ]}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg className="h-4 w-4 text-current opacity-70" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        );
      case 'lesson_progress':
        return (
          <div className="flex flex-col">
            {deal.course_id && deal.total_lessons > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>{deal.completed_lessons || 0}/{deal.total_lessons}</span>
                  <span className="text-gray-500">
                    {Math.round(((deal.completed_lessons || 0) / deal.total_lessons) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full" 
                    style={{ width: `${Math.min(100, Math.round((deal.completed_lessons || 0) / deal.total_lessons * 100))}%` }}
                  ></div>
                </div>
              </>
            ) : (
              <span className="text-gray-500 text-xs">—</span>
            )}
          </div>
        );
      case 'owner_name':
        return (
          <div className="text-sm text-gray-900">{deal.owner_name || '-'}</div>
        );
      case 'created_at':
        return (
          <div className="text-sm text-gray-500">
            {deal.created_at ? new Date(deal.created_at).toLocaleDateString() : '-'}
          </div>
        );
      case 'paid_date':
        return (
          <div className="text-sm text-gray-500">
            {deal.paid_date ? new Date(deal.paid_date).toLocaleDateString() : '-'}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <SharedTable
      data={deals}
      columns={columns}
      selectedItems={selectedDeals}
      onSelectItem={handleDealSelection}
      selectAll={selectAll}
      onSelectAll={handleSelectAll}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      renderCell={renderCell}
      loading={loading}
      emptyMessage="No deals found. Create your first deal to get started."
      resizable={true}
      onColumnsChange={setColumns}
      showColumnManagement={showColumnModal}
      onToggleColumnManagement={setShowColumnModal}
      pagination={pagination}
      onPageChange={onPageChange}
      onLimitChange={onLimitChange} // Pass onLimitChange down to SharedTable
      onResetColumns={handleResetColumns}
      tableHeight="calc(100vh - 240px)" // Match the height used in leads view to reduce unused space
    />
  );
};

export default DealsTableWithShared;
